import React from 'react'
import * as Context from 'fw/system/Context'
import * as FW from 'fw/system/FW'
import * as Block from 'fw/globals/Block'
import * as Modals from 'fw/globals/Modals'
import * as Guid from 'fw/misc/Guid'
import * as Logging from 'fw/system/Logging'

const _defaultOptions = {
    block: true
}

class API {
    constructor(serviceName, endpoint) {
        this.serviceName = serviceName;
        this.endpoint = endpoint || "";
    }

    url = (url) => {
        return this.endpoint + url;
    }

    post = (url, payload, callbacks, options) => {
        return this.call(url, 'POST', payload, callbacks, options);
    }

    get = (url, payload, callbacks, options) => {
        return this.call(url, 'GET', payload, callbacks, options);
    }

    call = (url, method, payload, callbacks, options) => {
        // Get JWT token from the storage
        let token = Context.get("token");
        let trace = Guid.generate();

        // Add content & auth headers
        let headers = {
            'X-PSX-TRACE': trace,
            'Content-Type': 'application/json',
            'Authorization': "Bearer " + token
        };

        // Use default options
        let opts = Object.assign({}, _defaultOptions);

        if (options)
            opts = Object.assign(opts, options);

        if (opts.block)
            Block.block();

        let body;
        let queryString = "";
        let cacheKey = "";

        if (method === 'POST')
            body = JSON.stringify(payload);
        else if (method === 'GET')
            queryString = "?" + this.toQueryString(payload) + '&' + cacheKey;

        const requestedUri = this.endpoint + url + queryString;

        // Using fetch
        return fetch(requestedUri, {
            method: method,
            headers: headers,
            body: body
        })
            .then((response) => {

                if (opts.block)
                    Block.unblock();

                // We only parse response if status was OK
                if (response.ok) {
                    return response.json()
                } else {
                    // Unauthorized? Go to login...
                    if (response.status === 401) {
                        window.location.href = FW.getSettings().loginRoute;
                        return null;
                    }

                    // Forbidden? Go to login...
                    if (response.status === 403) {
                        Modals.alert('You do not have permissions to access', function () {
                            window.location.href = "/";
                        });
                        return null;
                    }


                    // Server error?
                    if (response.status === 500) {
                        response.json().then(body => console.log("Error Info", body));
                    }

                    // For network errors and alike, we'll capture the
                    // fields and throw a new exception
					// eslint-disable-next-line 
                    throw {
                        status: response.status,
                        statusText: response.statusText,
                        url: response.url
                    };
                }
            })
            .then((json) => {
                // We added this after placing the login redirect
                if (json === null)
                    return null;
                
                // Reset last server call counter
                Context.set("lastServerCall", new Date());

                // If the service response was error, call the required callbacks
                // Same if it was sucessfull
                if (!json.status) {
                    if (callbacks !== undefined && callbacks.error !== undefined)
                        return callbacks.error(json);
                } else {
                    if (callbacks !== undefined && callbacks.success !== undefined)
                        return callbacks.success(json);
                }
            })
            .catch(err => {
                console.error('Exception', err, trace);

                if (opts.block)
                    Block.unblock();

                // Log
                const errorMessage = `${method} request from API.call() to ${requestedUri} got: ${err}`;
                Logging.error(errorMessage, trace);

                if (callbacks !== undefined && callbacks.exception !== undefined)
                    callbacks.exception(err)
                else
                    Modals.inline(
                        <div>
                            <h1 className="page-header">Sorry, something went wrong.</h1>
                            <p>It looks like there was some error trying to process your last action.<br />
                                Please, try again. If the problem persists contact an administrator.<br /><br />
                                <span style={{ fontFamily: "courier new", fontSize: "11px" }}>Ref: {trace}</span></p>
                        </div>);

            });
    }

    downloadEncrypted = (file) => {
        // Get JWT token from the storage
        let token = Context.get("token");
        let trace = Guid.generate();

        // Add content & auth headers
        let headers = {
            'X-PSX-TRACE': trace,
            'Content-Type': 'application/json',
            'Authorization': "Bearer " + token
        };

        let body;
        let queryString = "";
        let cacheKey = "";

        var url = FW.getSettings().fileDownloadEncryptedUrl;
        var payload = {
            storageToken: file.storageToken,
            fileName: file.fileName,
            useEncryption: file.useEncryption
        };

        queryString = "?" + this.toQueryString(payload) + '&' + cacheKey;

        const requestedUri = this.endpoint + url + queryString;

        // Using fetch
        return fetch(requestedUri, {
            method: 'GET',
            headers: headers,
            body: body
        })
            .then(response => {
                response.blob().then(blob => {
                    let url = window.URL.createObjectURL(blob);
                    let a = document.createElement('a');
                    a.href = url;
                    a.download = file.fileName;
                    a.click();
                })
            });
    }


    download = (file) => {
        this.post(
            FW.getSettings().fileDownloadUrl,
            {
                storageToken: file.storageToken,
                fileName: file.fileName,
                useEncryption: file.useEncryption
            },
            {
                success: (r) => {
                    let a = document.createElement("a");
                    a.href = r.data.directLink;
                    a.download = file.fileName;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                },
                error: (r) => {
                    this.props.onError(r.error);
                }
            },
            {
                block: false
            });
    }

    raw = (url, payload, callbacks) => {
        // Get JWT token from context
        let token = Context.get("token");

        let headers = {
            'Content-Type': 'application/json',
            'Authorization': "Bearer " + token
        };

        const requested_uri = this.endpoint + url;

        return fetch(requested_uri, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload)
        })
            .then((response) => {
                if (!response) {
                    if (callbacks !== undefined && callbacks.error !== undefined)
                        return callbacks.error(response);
                } else {
                    if (callbacks !== undefined && callbacks.success !== undefined)
                        return callbacks.success(response);
                }
            })
            .catch(err => {
                console.error('Exception', err);

                // Log
                const errorMessage = `POST request from API.raw() to ${requested_uri} got: ${err}`;
                Logging.error(errorMessage, null);

                if (callbacks !== undefined && callbacks.exception !== undefined)
                    callbacks.exception(err)
                else
                    Modals.inline(
                        <div>
                            <h1 className="page-header">Sorry, something went wrong.</h1>
                            <p>It looks like there was some error trying to process your last action.<br />Please, try again. If the problem persists contact an administrator.</p>
                        </div>);
            });
    }

    upload = (url, formData, callbacks) => {
        // Get JWT token from the storage
        let token = Context.get("token");

        // Add content & auth headers
        let headers = {
            'Authorization': "Bearer " + token
        };

        // Using fetch
        const requested_uri = this.endpoint + url;
        return fetch(requested_uri, {
            method: 'POST',
            headers: headers,
            body: formData
        })
            .then((response) => {
                // We only parse response if status was OK
                if (response.ok) {
                    return response.json()
                } else {
                    // For network errors and alike, we'll capture the
                    // fields and throw a new exception
					// eslint-disable-next-line 
                    throw {
                        status: response.status,
                        statusText: response.statusText,
                        url: response.url
                    };
                }
            })
            .then((json) => {
                // If the service response was error, call the required callbacks
                // Same if it was sucessfull
                if (!json.status) {
                    if (callbacks.error !== undefined)
                        return callbacks.error(json);
                } else {
                    if (callbacks.success !== undefined)
                        return callbacks.success(json);
                }
            })
            .catch(err => {
                console.error('Exception', err);

                // Log
                const errorMessage = `POST request from API.upload() to ${requested_uri} got: ${err}`;
                Logging.error(errorMessage, null);

                if (callbacks.exception !== undefined)
                    callbacks.exception(err)
                else
                    Modals.inline(
                        <div>
                            <h1 className="page-header">Sorry, something went wrong.</h1>
                            <p>It looks like there was some error trying to process your last action.<br />Please, try again. If the problem persists contact an administrator.</p>
                        </div>);
            });
    }

    toQueryString = (o) => {
        if (o === undefined || o === null)
            return "";

        return Object
            .keys(o)
            .filter(key => o[key] !== null)
            .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(o[key])}`)
            .join('&');
    }
}

export default API



// WEBPACK FOOTER //
// ./src/fw/system/API.js